import { Link } from "@tanstack/react-router";
import CheckCircle from "~icons/material-symbols/check-circle";
import Info from "~icons/material-symbols/info-outline";
import { css } from "../../../../styled-system/css";
import { t } from "../../../libs/i18n";
import type { R7MiyagiCampaignBannerType } from "../utils/r7-miyagi-campaign-banner";

type R7MiyagiCampaignBannerProps = {
  bannerType: R7MiyagiCampaignBannerType;
  onClickApply: () => void;
};

export const R7MiyagiCampaignBanner = ({
  bannerType,
  onClickApply,
}: R7MiyagiCampaignBannerProps) => {
  if (bannerType === "hidden") {
    return null;
  }

  return (
    <section
      className={css({
        bg: "surface.accentPrimaryLight",
        rounded: "xl",
        p: "16px",
        display: "flex",
        flexDir: "column",
        gap: "16px",
      })}
    >
      {bannerType === "not-applied" && (
        <NotAppliedContent onClickApply={onClickApply} />
      )}
      {bannerType === "campaign-ended" && <CampaignEndedContent />}
      {bannerType === "applied" && <AppliedContent />}
      {bannerType === "received" && <ReceivedContent />}

      <div
        className={css({
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
        })}
      >
        <Link
          to="/about-campaign"
          className={css({
            flexGrow: 1,
            color: "text.accentPrimary",
            textDecoration: "underline",
          })}
        >
          {t("キャンペーン詳細")}
        </Link>
      </div>
    </section>
  );
};

const NotAppliedContent = ({ onClickApply }: { onClickApply: () => void }) => (
  <>
    <div
      className={css({
        display: "flex",
        flexDir: "column",
        gap: "10px",
      })}
    >
      <div
        className={css({
          display: "flex",
          alignItems: "center",
          gap: "6px",
        })}
      >
        <Info
          className={css({
            color: "text.accentPrimary",
            flexShrink: 0,
          })}
        />
        <p>
          {t("令和8年6月30日までの期間、ポイント付与キャンペーンを実施中！")}
        </p>
      </div>

      <p
        className={css({
          color: "text.secondary",
          fontSize: "14px",
        })}
      >
        {t(
          "「みやぎ防災」ミニアプリに登録した方に、その場で3,000ポイントをプレゼント！",
        )}
      </p>
    </div>

    <button
      type="button"
      className={css({
        bg: "surface.accentPrimary",
        rounded: "2xl",
        px: "14px",
        py: "12px",
        color: "white",
        fontWeight: 500,
      })}
      onClick={onClickApply}
    >
      {t("応募する")}
    </button>
  </>
);

const CampaignEndedContent = () => (
  <div
    className={css({
      display: "flex",
      flexDir: "column",
      gap: "10px",
    })}
  >
    <div
      className={css({
        display: "flex",
        alignItems: "center",
        gap: "6px",
      })}
    >
      <Info
        className={css({
          color: "text.accentPrimary",
          flexShrink: 0,
        })}
      />
      <p>{t("ポイント付与キャンペーンは終了しました")}</p>
    </div>

    <p
      className={css({
        color: "text.secondary",
        fontSize: "14px",
      })}
    >
      {t(
        "新規登録者が40万名に達したため、令和7年度の3,000ポイントプレゼントキャンペーンは終了しました。",
      )}
    </p>
  </div>
);

const AppliedContent = () => (
  <>
    <div
      className={css({
        display: "flex",
        flexDir: "column",
        gap: "10px",
      })}
    >
      <div
        className={css({
          display: "flex",
          alignItems: "center",
          gap: "6px",
        })}
      >
        <Info
          className={css({
            color: "text.accentPrimary",
            flexShrink: 0,
          })}
        />
        <p>
          {t("令和8年6月30日までの期間、ポイント付与キャンペーンを実施中！")}
        </p>
      </div>

      <p
        className={css({
          color: "text.secondary",
          fontSize: "14px",
        })}
      >
        {t(
          "「みやぎ防災」ミニアプリに登録した方に、その場で3,000ポイントをプレゼント！",
        )}
      </p>
    </div>

    <button
      type="button"
      className={css({
        bg: "surface.disable",
        rounded: "2xl",
        px: "14px",
        py: "12px",
        color: "text.disable",
        fontWeight: 500,
      })}
      disabled
    >
      {t("応募する")}
    </button>

    <div
      className={css({
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
      })}
    >
      <CheckCircle
        className={css({
          color: "text.accentSuccess",
          width: "20px",
          height: "20px",
        })}
      />
      <p
        className={css({
          color: "text.accentSuccess",
          fontSize: "14px",
        })}
      >
        {t("応募済み")}
      </p>
    </div>
  </>
);

const ReceivedContent = () => (
  <div
    className={css({
      display: "flex",
      flexDir: "column",
      gap: "10px",
    })}
  >
    <div
      className={css({
        display: "flex",
        alignItems: "center",
        gap: "6px",
      })}
    >
      <Info
        className={css({
          color: "text.accentPrimary",
          flexShrink: 0,
        })}
      />
      <p>{t("令和8年6月30日までの期間、ポイント付与キャンペーンを実施中！")}</p>
    </div>

    <p
      className={css({
        color: "text.secondary",
        fontSize: "14px",
      })}
    >
      {t(
        "R6年度キャンペーンに応募済みのため、本キャンペーンの対象外となります。",
      )}
    </p>
  </div>
);
